// Date and time formatting utilities with timezone support
import { useSettingsStore } from '../stores/settingsStore'

/**
 * Common timezone options organized by region
 */
export const TIMEZONES = {
  'UTC': [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  ],
  'North America': [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  ],
  'Europe': [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
    { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
    { value: 'Europe/Brussels', label: 'Brussels (CET/CEST)' },
    { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
    { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  ],
  'Asia': [
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  ],
  'Australia & Pacific': [
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  ],
  'South America': [
    { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
    { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
    { value: 'America/Santiago', label: 'Santiago (CLT)' },
  ],
  'Africa': [
    { value: 'Africa/Cairo', label: 'Cairo (EET)' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
    { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  ],
}

/**
 * Get the current timezone from settings store
 */
export const getTimezone = (): string => {
  return useSettingsStore.getState().timezone
}

/**
 * Format a date to locale date string in the selected timezone
 * @param date - Date string or Date object
 * @param timezone - Optional timezone override
 * @returns Formatted date string (e.g., "2/15/2026")
 */
export const formatDate = (
  date: string | Date,
  timezone?: string
): string => {
  const tz = timezone || getTimezone()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  return dateObj.toLocaleDateString(undefined, {
    timeZone: tz,
  })
}

/**
 * Format a date to locale time string in the selected timezone
 * @param date - Date string or Date object
 * @param timezone - Optional timezone override
 * @returns Formatted time string (e.g., "3:45:30 PM")
 */
export const formatTime = (
  date: string | Date | null | undefined,
  timezone?: string
): string => {
  if (!date) return 'Just now'
  const tz = timezone || getTimezone()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(dateObj.getTime())) return 'Just now'
  
  return dateObj.toLocaleTimeString(undefined, {
    timeZone: tz,
  })
}

/**
 * Format a date to locale date and time string in the selected timezone
 * @param date - Date string or Date object
 * @param timezone - Optional timezone override
 * @returns Formatted date and time string (e.g., "2/15/2026, 3:45:30 PM")
 */
export const formatDateTime = (
  date: string | Date,
  timezone?: string
): string => {
  const tz = timezone || getTimezone()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  return dateObj.toLocaleString(undefined, {
    timeZone: tz,
  })
}

/**
 * Format a date with custom options in the selected timezone
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @param timezone - Optional timezone override
 * @returns Formatted date string
 */
export const formatDateCustom = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions,
  timezone?: string
): string => {
  const tz = timezone || getTimezone()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  return dateObj.toLocaleString(undefined, {
    ...options,
    timeZone: tz,
  })
}

/**
 * Get all available timezones as a flat array
 */
export const getAllTimezones = () => {
  return Object.entries(TIMEZONES).flatMap(([region, zones]) =>
    zones.map(zone => ({ ...zone, region }))
  )
}

/**
 * Find timezone info by value
 */
export const getTimezoneInfo = (timezoneValue: string) => {
  for (const zones of Object.values(TIMEZONES)) {
    const found = zones.find(tz => tz.value === timezoneValue)
    if (found) return found
  }
  return null
}
